import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import Breadcrumbs, { type BreadcrumbItem } from "@/components/feature/Breadcrumbs";
import type { FarmerProfile } from "@/mocks/farmerProfiles";

interface FarmerDetailHeroProps {
  farmer: FarmerProfile;
  breadcrumbs: BreadcrumbItem[];
}

export default function FarmerDetailHero({ farmer, breadcrumbs }: FarmerDetailHeroProps) {
  return (
    <section className="relative bg-primary-950 pt-28 md:pt-32 pb-16 md:pb-20 overflow-hidden">
      <div className="absolute inset-0 grain opacity-30"></div>
      <div className="relative w-full px-4 md:px-8">
        <Breadcrumbs
          items={breadcrumbs}
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <div className="rounded-lg overflow-hidden w-full h-[24rem] md:h-[32rem] border border-background-50/10">
              <img
                src={farmer.portrait}
                width={1000}
                height={1200}
                alt={`Portrait of ${farmer.name}, a farmer from ${farmer.village}`}
                title={`${farmer.name} - ${farmer.crop} farmer in ${farmer.village}`}
                className="w-full h-full object-cover object-top"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={100}>
            <div className="flex items-center gap-3 mb-5">
              <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-400"></span>
              <span className="text-xs font-semibold tracking-[0.28em] uppercase text-accent-300">
                {farmer.farmingFor}
              </span>
            </div>

            <h1 className="font-heading text-4xl md:text-6xl leading-tight text-background-50 font-semibold">
              {farmer.name}
            </h1>

            <p className="font-heading text-xl md:text-2xl italic text-accent-300 mt-5 max-w-2xl">
              "{farmer.quote}"
            </p>

            <p className="mt-6 text-base md:text-lg text-background-200 leading-relaxed max-w-2xl">
              {farmer.intro}
            </p>

            <div className="flex flex-wrap gap-x-8 gap-y-4 mt-9">
              <div className="border-l-2 border-accent-500/70 pl-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-background-300">
                  Village
                </p>
                <p className="text-sm md:text-base font-medium text-background-50 mt-1 flex items-center gap-2">
                  <i className="ri-map-pin-line text-accent-300"></i>
                  {farmer.village}
                </p>
              </div>
              <div className="border-l-2 border-accent-500/70 pl-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-background-300">
                  Grows
                </p>
                <p className="text-sm md:text-base font-medium text-background-50 mt-1 flex items-center gap-2">
                  <i className="ri-plant-line text-accent-300"></i>
                  {farmer.crop}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}